import { fromJS } from 'immutable';
import _, { get } from 'lodash';
import { createSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import {
  makeGetFeatureFlag,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectJvpField,
} from 'Containers/App/selectors';
import { makeGetCoverageTier, makeGetTierCodeData, makeSelectSurvey } from 'Containers/ProfilePage/selectors';
import { memberIdsListToMemberIdsKey } from 'Containers/ViewBenefitPage/utils';
import Plan from 'Models/plan';
import {
  CoveredMember,
  FieldDataTypes,
  REASONS_WHY,
  ReasonsWhy,
  Recommendation,
  SelectedPlansCoveredMembers,
  SupplementalPlansFullHousehold,
  SupplementalRecommendations,
} from 'Types/entities';
import { ElectionInput } from 'Utils/apiTypes';
import { getFsaDecisionForSelectedPlan, isLimitedPurposeFsaForSelectedPlan } from 'Utils/helpers';

import { SUPPLEMENTAL_ELECTIONS } from './constants';
import { OverviewPageReducerState } from './reducer';

const selectOverviewPageDomain = (state: GlobalReducerState) => fromJS(state).get('overviewPage');

const selectOverviewDomainAsJS = (state: GlobalReducerState): OverviewPageReducerState => state.overviewPage;

const makeSelectOverviewField = <K extends keyof OverviewPageReducerState>(field: K) =>
  createSelector(
    selectOverviewDomainAsJS,
    (substate: OverviewPageReducerState): OverviewPageReducerState[K] => substate[field],
  );

const makeGetSelectedPlansCount = () =>
  createSelector(
    [makeGetIsHealthPlanSelected(), makeSelectOverviewField('selected_plans')],
    (isHealthPlanSelected, selectedSupplementalPlans) =>
      Object.values(selectedSupplementalPlans).filter((x) => x !== null).length +
      (isHealthPlanSelected ? 1 : 0),
  );

const makeSelectSupplementalPlansForMemberIdsFromCache = (memberIds: string[]) =>
  createSelector(makeSelectOverviewField('supplemental_plans_household_subsets_cache'), (cache) => {
    const memberIdsKey = memberIdsListToMemberIdsKey(memberIds);
    const membersExist = Object.keys(cache).indexOf(memberIdsKey) >= 0;

    if (membersExist) {
      return cache[memberIdsKey];
    }
    return null;
  });

const getRiskAversion = () =>
  createSelector([makeSelectSurvey()], ({ risk_question_1, risk_question_2 }) => {
    if (risk_question_1 === 'plan_a') {
      return 'high';
    }
    if (risk_question_1 === 'plan_b' && risk_question_2 === 'plan_b') {
      return 'low';
    }
    return 'medium';
  });

const makeGetSelectedHealthDeductible = () =>
  createSelector(
    [makeSelectCommercialField('selectedHealthPlan'), makeGetCoverageTier()],
    (healthPlan, coverageTier) => {
      if (!_.isEmpty(healthPlan)) {
        return new Plan(healthPlan as Recommendation).getInNetworkDeductible(coverageTier);
      }
      return 0;
    },
  );

const makeGetSupplementalPlanRecommendations = () =>
  createSelector(
    [
      makeSelectOverviewField('supplemental_plans_full_household'),
      makeSelectConfigField('enable_fbs_recommendations'),
      makeSelectConfigField('enable_fbs_best_match'),
      makeSelectConfigField('enable_fbs_recommendation_types'),
    ],
    (
      supplementalPlans,
      enableFbsRecommendations,
      enableFbsBestMatch,
      enableFbsRecommendationTypes,
    ): SupplementalRecommendations => {
      let bestScore = -1;
      let bestMatch = '';
      let recommendedCount = 0;
      const recommendations: SupplementalRecommendations = {};

      Object.keys(supplementalPlans).forEach((benefitType) => {
        const benefitRecEnabled =
          benefitType in enableFbsRecommendationTypes && enableFbsRecommendationTypes[benefitType];

        // FBS Recs can be disabled wholesale or per-benefit-type
        if (!enableFbsRecommendations || !benefitRecEnabled) {
          return;
        }

        const plans = supplementalPlans[benefitType].plans;
        const firstPlan = plans[0];

        if (firstPlan.score_type === 'always_recommend') {
          recommendations[benefitType] = {
            score: firstPlan.score,
            score_type: firstPlan.score_type,
            label: 'recommended',
          };
        } else {
          const highestScore = plans
            .map((plan) => plan.score || -1)
            .sort()
            .reverse()[0];

          if (highestScore > 0) {
            recommendations[benefitType] = {
              score: highestScore,
              score_type: firstPlan.score_type,
              label: 'recommended',
            };

            recommendedCount += 1;

            if (highestScore > bestScore) {
              bestScore = highestScore;
              bestMatch = benefitType;
            }
          } else if (firstPlan.score_type) {
            // Only include "scored" plan types
            recommendations[benefitType] = {
              score: 0,
              score_type: firstPlan.score_type,
              label: 'not_recommended',
            };
          }
        }
      });

      // We only want 'best match' if:
      // - multiple benefits are recommended
      // - best score is > 1
      // - best match config flag is enabled
      if (recommendedCount > 1 && bestScore > 1 && enableFbsBestMatch) {
        recommendations[bestMatch].label = 'best';
      }

      return recommendations;
    },
  );

const makeGetIsLimitedPurposeFsa = () =>
  createSelector(
    [
      makeSelectOverviewField('supplemental_plans_full_household'),
      makeSelectCommercialField('selectedHealthPlan'),
    ],
    (supplementalPlans, selectedHealthPlan): boolean =>
      isLimitedPurposeFsaForSelectedPlan(selectedHealthPlan, supplementalPlans),
  );

const makeGetSupplementalPlanReasonsWhy = () =>
  createSelector(
    [makeSelectOverviewField('supplemental_plans_full_household'), makeGetIsLimitedPurposeFsa()],
    (supplementalPlans, isLimitedPurposeFsa): ReasonsWhy => {
      const reasons = {};

      Object.keys(supplementalPlans).forEach((benefitType) => {
        const plans = supplementalPlans[benefitType].plans;

        // NOTE: Only look for reasons for the first plan of this type, they should be the same if there are multiple
        reasons[benefitType] = plans[0].reasons?.map((plan) => plan.code) || [];
        if (benefitType === 'ltc') {
          reasons[benefitType] = [REASONS_WHY.GROUP_RATE_ADVANTAGE, REASONS_WHY.CASH_VALUE_RETENTION];
        }
        if (benefitType === 'fsa' || benefitType === 'fsa_medical') {
          reasons[benefitType] = isLimitedPurposeFsa
            ? [REASONS_WHY.FSA_MEDICAL_LIMITED_PURPOSE]
            : [
                REASONS_WHY.FSA_MEDICAL_GENERIC,
                REASONS_WHY.FSA_MEDICAL_EXTRA,
                ...(reasons[benefitType] || []),
              ];
        }
        if (benefitType === 'fsa_dependent') {
          reasons[benefitType] = [REASONS_WHY.FSA_DEPENDENT_TAX, REASONS_WHY.FSA_DEPENDENT_SCOPE];
        }
      });

      return reasons;
    },
  );

const makeGetSupplementalPremiums = () =>
  createSelector(makeSelectOverviewField('selected_plans'), (selectedPlans): number => {
    let total = 0;

    Object.keys(selectedPlans).forEach((key) => {
      const value = selectedPlans[key];
      if (value) {
        const monthlyPremium = value.monthly_premium;
        if (monthlyPremium !== null && !_.isEmpty(value.monthly_premium)) {
          if (monthlyPremium.type === FieldDataTypes.USD) {
            total += monthlyPremium.data;
          }
        }
      }
    });

    return total;
  });

const makeGetIsHealthPlanSelected = () =>
  createSelector(makeSelectCommercialField('selectedHealthPlan'), (healthPlan) => {
    if (!_.isEmpty(healthPlan)) return true;

    return false;
  });

const makeGetHsaPlan = () =>
  createSelector(makeSelectOverviewField('supplemental_plans_full_household'), (supplementalPlans) => {
    // eslint-disable-next-line no-prototype-builtins
    if (supplementalPlans.hasOwnProperty('hsa')) {
      return supplementalPlans.hsa?.plans[0] || null;
    }

    return null;
  });

const makeGetHraPlan = () =>
  createSelector(makeSelectOverviewField('supplemental_plans_full_household'), (supplementalPlans) => {
    // eslint-disable-next-line no-prototype-builtins
    if (supplementalPlans.hasOwnProperty('hra')) {
      return supplementalPlans.hra?.plans[0] || null;
    }

    return null;
  });

const makeGetIsFsaEligible = () =>
  createSelector(
    [
      makeSelectOverviewField('supplemental_plans_full_household'),
      makeSelectCommercialField('selectedHealthPlan'),
    ],
    (supplementalPlans, selectedHealthPlan) =>
      getFsaDecisionForSelectedPlan(selectedHealthPlan, supplementalPlans).buttonVisible,
  );

const makeGetFsaMedicalPlan = () =>
  createSelector(makeSelectOverviewField('supplemental_plans_full_household'), (supplementalPlans) => {
    // eslint-disable-next-line no-prototype-builtins
    if (supplementalPlans.hasOwnProperty('fsa_medical')) {
      return supplementalPlans.fsa_medical?.plans[0] || null;
    }

    return null;
  });

const makeGetShowTaxSavings = () =>
  createSelector(
    [
      makeSelectConfigField('show_tax_savings_page'),
      makeSelectCommercialField('selectedHealthPlan'),
      makeGetIsFsaEligible(),
    ],
    (taxSavingsEnabled, selectedPlan, isFsaEligible) =>
      taxSavingsEnabled && (get(selectedPlan, 'plan.hsa_eligible', false) || isFsaEligible),
  );

const makeGetSupplementalPlanElections = (isReviewPageLoaded: boolean) =>
  createSelector(
    [
      makeSelectOverviewField('selected_plans'),
      makeGetTierCodeData(),
      makeSelectOverviewField('supplemental_plans_full_household'),
      makeSelectOverviewField('selected_plans_covered_members'),
      makeSelectJvpField('userId'),
      makeGetFeatureFlag('is_election_members_enabled'),
    ],
    (
      selectedPlans,
      tierCodeData,
      supplementalPlans: SupplementalPlansFullHousehold,
      selectedPlanCoveredMembers: SelectedPlansCoveredMembers,
      userId: string | undefined,
      electionMembersEnabled: boolean | null,
    ): ElectionInput[] => {
      const elections: ElectionInput[] = [];
      const [tierCode, tierCodeDescription] = tierCodeData;
      Object.keys(SUPPLEMENTAL_ELECTIONS).forEach((type) => {
        const suppMembers = selectedPlanCoveredMembers[type] || [];

        if (selectedPlans[type]) {
          const election: ElectionInput = {
            planable_type: SUPPLEMENTAL_ELECTIONS[type],
            jv_plan_id: selectedPlans[type]?.external_id,
            tier_code: tierCode,
            tier_code_description: tierCodeDescription,
          };

          if (electionMembersEnabled) {
            election.election_members = Object.values(suppMembers).map((m: CoveredMember) => ({
              member_id: m.policyholder && userId ? userId : m.memberId,
              member_type: m.policyholder ? 'User' : 'Dependent',
              is_covered: m.covered,
            }));
          }

          elections.push(election);
        } else if (isReviewPageLoaded) {
          // Waive coverage on plan types for which the user is eligible
          if (type in supplementalPlans && supplementalPlans[type]?.plans?.length > 0) {
            const election: ElectionInput = {
              planable_type: SUPPLEMENTAL_ELECTIONS[type],
              waived: true,
            };
            if (electionMembersEnabled) {
              election.election_members = [];
            }
            elections.push(election);
          }
        }
      });
      return elections;
    },
  );

export {
  selectOverviewPageDomain,
  selectOverviewDomainAsJS,
  makeSelectOverviewField,
  makeSelectSupplementalPlansForMemberIdsFromCache,
  makeGetSelectedPlansCount,
  makeGetSupplementalPremiums,
  makeGetSelectedHealthDeductible,
  makeGetSupplementalPlanRecommendations,
  makeGetSupplementalPlanReasonsWhy,
  makeGetIsLimitedPurposeFsa,
  makeGetIsHealthPlanSelected,
  makeGetHsaPlan,
  makeGetHraPlan,
  makeGetShowTaxSavings,
  makeGetIsFsaEligible,
  makeGetFsaMedicalPlan,
  getRiskAversion,
  makeGetSupplementalPlanElections,
};
